import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { getGPS, queueInspection, queuePhoto, queueAudio, syncPending } from "@/lib/offline";
import { compressImage } from "@/lib/image";
import { ArrowLeft, AlertOctagon, ShieldCheck } from "lucide-react";
import { MILESTONES, NUMERIC, DOMAINS, firstSkippedMilestone } from "./InspectionConstants";
import { ChecklistDomain } from "./ChecklistDomain";
import { DefectList } from "./DefectList";
import { PhotoCapture } from "./PhotoCapture";

export default function InspectionForm() {
  const { loomId } = useParams();
  const [sp] = useSearchParams();
  const isAudit = sp.get("audit") === "1";
  const nav = useNavigate();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [loom, setLoom] = useState(null);
  const [po, setPo] = useState(null);
  const [milestone, setMilestone] = useState(isAudit ? "Ad-hoc" : "25%");
  const [pct, setPct] = useState(25);
  const [length, setLength] = useState("");
  const [kpsi, setKpsi] = useState("");
  const [pile, setPile] = useState(["", "", ""]);
  const [width, setWidth] = useState("");
  const [checks, setChecks] = useState({});
  const [photos, setPhotos] = useState([]); // {id, blobUrl, gps_lat, gps_lng, taken_at, blob, uploaded_file_id?}
  const [defects, setDefects] = useState([]);
  const [grade, setGrade] = useState("A");
  const [gradeNote, setGradeNote] = useState("");
  const [voiceNote, setVoiceNote] = useState(null); // {blob, mime, duration_sec}
  const [gpsErr, setGpsErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [priorMilestones, setPriorMilestones] = useState([]);

  useEffect(() => {
    (async () => {
      // These GETs are best-effort: on a freshly assigned loom, the PO or prior-
      // inspection lookups can 403/404. That should not surface as a red React
      // ErrorOverlay covering the form's action buttons.
      try {
        const looms = await api.get("/looms");
        const l = looms.data.find((x) => x.id === loomId);
        setLoom(l);
        if (l?.current_po_id) {
          try {
            const pr = await api.get(`/pos/${l.current_po_id}`);
            setPo(pr.data);
          } catch (e) {
            console.debug("[InspectionForm] PO fetch skipped:", e?.response?.status);
          }
        }
      } catch (e) {
        console.debug("[InspectionForm] looms fetch failed:", e?.response?.status);
      }
      try {
        const ir = await api.get("/inspections", { params: { loom_id: loomId } });
        setPriorMilestones(ir.data.map((x) => x.milestone));
      } catch (e) {
        console.debug("[InspectionForm] prior inspections skipped:", e?.response?.status);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loomId]);

  useEffect(() => {
    // suggest grade from defects
    const anyCritical = defects.some((d) => d.class === "Critical");
    const anyMajor = defects.some((d) => d.class === "Major");
    const pace = po?.pace_flag;
    if (anyCritical || pace === "red") setGrade("C");
    else if (anyMajor || pace === "amber") setGrade("B");
    else setGrade("A");
  }, [defects, po]);

  useEffect(() => {
    if (NUMERIC.includes(milestone)) {
      setPct(parseInt(milestone));
    }
  }, [milestone]);

  const addPhoto = async (file) => {
    setGpsErr("");
    let gps;
    try {
      gps = await getGPS();
    } catch (e) {
      setGpsErr(t("gps_required"));
      return;
    }
    // Compress client-side (max 1280px, JPEG 70%)
    const compressed = await compressImage(file, { maxEdge: 1280, quality: 0.7 });
    const id = Math.random().toString(36).slice(2);
    const blobUrl = URL.createObjectURL(compressed);
    const p = {
      id,
      blob: compressed,
      blobUrl,
      size: compressed.size,
      gps_lat: gps.lat,
      gps_lng: gps.lng,
      taken_at: new Date().toISOString(),  // original capture timestamp
    };
    setPhotos((prev) => [...prev, p]);
  };

  const removePhoto = (id) => setPhotos((prev) => prev.filter((p) => p.id !== id));

  const addDefect = () => setDefects((prev) => [...prev, { class: "Minor", description: "", row_number: "" }]);
  const updateDefect = (i, patch) => setDefects((prev) => prev.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));
  const removeDefect = (i) => setDefects((prev) => prev.filter((_, idx) => idx !== i));

  const setCheck = (item, val) => setChecks((prev) => ({ ...prev, [item]: { ...(prev[item] || {}), status: val } }));
  const setCheckNote = (item, note) => setChecks((prev) => ({ ...prev, [item]: { ...(prev[item] || {}), note } }));

  const validateMilestoneSequence = () => firstSkippedMilestone(milestone, priorMilestones);

  const submit = async () => {
    setErr("");
    if (photos.length === 0) { setErr(t("validation_photo_required")); return; }
    const skipped = validateMilestoneSequence();
    if (skipped) { setErr(t("validation_milestone_skip") + ` (${skipped})`); return; }
    const anyCritical = defects.some((d) => d.class === "Critical");
    if (anyCritical) {
      // ensure at least 1 photo present (already checked)
    }
    setSaving(true);
    const online = navigator.onLine;
    const payload = {
      po_id: po?.id,
      loom_id: loomId,
      milestone,
      percent_complete: parseInt(pct),
      length_woven_cm: length ? parseFloat(length) : null,
      kpsi: kpsi ? parseFloat(kpsi) : null,
      pile_heights_mm: pile.map((p) => (p ? parseFloat(p) : null)),
      width_inches: width ? parseFloat(width) : null,
      checklist: checks,
      defects: defects.filter((d) => d.description),
      grade,
      grade_override_note: gradeNote,
      photos: [],
      is_audit: isAudit,
      audit_note: isAudit ? gradeNote : "",
    };

    try {
      if (online) {
        // upload photos
        const uploaded = [];
        for (const ph of photos) {
          const fd = new FormData();
          fd.append("file", ph.blob, `photo_${ph.id}.jpg`);
          fd.append("gps_lat", String(ph.gps_lat));
          fd.append("gps_lng", String(ph.gps_lng));
          fd.append("taken_at", ph.taken_at);
          const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          uploaded.push({ file_id: res.data.file_id, gps_lat: res.data.gps_lat, gps_lng: res.data.gps_lng, taken_at: res.data.taken_at });
        }
        payload.photos = uploaded;
        // upload voice note if any
        if (voiceNote?.blob) {
          const fd = new FormData();
          const ext = voiceNote.mime.includes("webm") ? "webm" : "mp4";
          fd.append("file", voiceNote.blob, `note_${Date.now()}.${ext}`);
          fd.append("taken_at", new Date().toISOString());
          const vres = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          payload.voice_note_file_id = vres.data.file_id;
        }
        await api.post("/inspections", payload);
        nav(-1);
      } else {
        // queue offline — persist blobs + payload in IndexedDB
        const capturedAt = new Date().toISOString();
        const localIds = [];
        for (const ph of photos) {
          await queuePhoto({ id: ph.id, blob: ph.blob, gps_lat: ph.gps_lat, gps_lng: ph.gps_lng, taken_at: ph.taken_at });
          localIds.push(ph.id);
        }
        payload.photos = photos.map((p) => ({ file_id: null, gps_lat: p.gps_lat, gps_lng: p.gps_lng, taken_at: p.taken_at, pending: true }));
        // queue voice note blob (if any) — survives force-close
        let localAudioId = null;
        if (voiceNote?.blob) {
          localAudioId = Math.random().toString(36).slice(2);
          await queueAudio({
            id: localAudioId,
            blob: voiceNote.blob,
            mime: voiceNote.mime,
            duration_sec: voiceNote.duration_sec,
            taken_at: capturedAt,
          });
        }
        await queueInspection({
          id: Math.random().toString(36).slice(2),
          payload,
          local_photo_ids: localIds,
          local_audio_id: localAudioId,
          queued_at: capturedAt,
          original_date: capturedAt,
          attempts: 0,
        });
        syncPending(api).catch(() => {});
        nav("/inspector/route");
      }
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.detail || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  if (!loom) return <div>{t("loading")}</div>;

  return (
    <div className="max-w-2xl">
      <button data-testid="ins-back-btn" onClick={() => nav(-1)} className="text-sm flex items-center gap-1 mb-3 text-[#4A627A]">
        <ArrowLeft size={16} /> Back
      </button>

      <div className="hag-surface p-4">
        <div className="eyebrow flex items-center gap-2">
          {isAudit && <span data-testid="audit-badge" className="status-chip pace-amber flex items-center gap-1"><ShieldCheck size={12} /> AUDIT</span>}
          {t("inspection_form")}
        </div>
        <div className="text-xl font-bold" style={{ color: "var(--brand-navy)" }}>{loom.loom_number} · {loom.weaver_name}</div>
        {po && <div className="text-xs text-[#7C8E9E]">{po.po_number}</div>}
      </div>

      {/* Milestone */}
      <Section title={t("milestone")}>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {MILESTONES.map((m) => (
            <button
              key={m}
              data-testid={`milestone-${m}`}
              onClick={() => setMilestone(m)}
              className={`tap-target rounded-md px-2 text-sm font-semibold border ${milestone === m ? "bg-[#1B3A5C] text-white border-[#1B3A5C]" : "bg-white text-[#1B3A5C] border-[#1B3A5C]/15"}`}
            >
              {m}
            </button>
          ))}
        </div>
      </Section>

      {/* Measurements */}
      <Section title="Measurements">
        <label className="text-xs uppercase tracking-widest font-bold text-[#4A627A]">{t("percent_complete")}: {pct}%</label>
        <input data-testid="pct-slider" type="range" min="0" max="100" value={pct} onChange={(e) => setPct(e.target.value)} className="w-full" />
        <Field label={t("length_woven")} value={length} onChange={setLength} type="number" testId="length-input" />
        <Field label={t("kpsi")} value={kpsi} onChange={setKpsi} type="number" testId="kpsi-input" />
        <Field label={t("width_inches")} value={width} onChange={setWidth} type="number" testId="width-input" />
        <label className="text-xs uppercase tracking-widest font-bold text-[#4A627A] mt-2 block">{t("pile_heights")}</label>
        <div className="grid grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <input
              key={i}
              data-testid={`pile-${i}`}
              value={pile[i]}
              onChange={(e) => setPile((p) => { const n = [...p]; n[i] = e.target.value; return n; })}
              type="number"
              step="0.1"
              className="h-12 px-2 rounded-md border border-[#1B3A5C]/15 text-base"
            />
          ))}
        </div>
      </Section>

      {/* Checklist */}
      {DOMAINS.map((dom) => (
        <ChecklistDomain
          key={dom.key}
          domain={dom}
          checks={checks}
          onSetCheck={setCheck}
          onSetNote={setCheckNote}
        />
      ))}

      {/* Photos + voice */}
      <PhotoCapture
        photos={photos}
        onAdd={addPhoto}
        onRemove={removePhoto}
        gpsErr={gpsErr}
        onVoiceRecorded={setVoiceNote}
        onVoiceRemoved={() => setVoiceNote(null)}
      />

      {/* Defects */}
      <DefectList
        defects={defects}
        onAdd={addDefect}
        onUpdate={updateDefect}
        onRemove={removeDefect}
      />

      {/* Grade */}
      <Section title={t("grade")}>
        <div className="flex gap-2">
          {["A", "B", "C"].map((g) => (
            <button
              key={g}
              data-testid={`grade-${g}`}
              onClick={() => setGrade(g)}
              className={`tap-target flex-1 h-14 rounded-md text-lg font-bold border ${grade === g ? (g === "A" ? "grade-A" : g === "B" ? "grade-B" : "grade-C") : "bg-white border-[#1B3A5C]/15"}`}
            >
              {g}
            </button>
          ))}
        </div>
        <input
          data-testid="grade-note"
          placeholder={t("grade_note")}
          value={gradeNote}
          onChange={(e) => setGradeNote(e.target.value)}
          className="mt-2 w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm"
        />
      </Section>

      {err && <div data-testid="ins-form-err" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-2 my-3">{err}</div>}

      <button
        data-testid="submit-inspection-btn"
        onClick={submit}
        disabled={saving}
        className="w-full h-14 rounded-md font-bold text-white text-lg mt-4"
        style={{ background: "var(--brand-navy)" }}
      >
        {saving ? t("loading") : t("submit")}
      </button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="hag-surface p-4 mt-4">
      <h3 className="text-sm font-bold uppercase tracking-widest text-[#4A627A] mb-3">{title}</h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest font-bold text-[#4A627A] mt-2 block">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        step="0.1"
        className="w-full h-12 px-3 rounded-md border border-[#1B3A5C]/15 text-base"
      />
    </div>
  );
}
